import { useRef } from 'react'
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion'

const EASE = [0.16, 1, 0.3, 1]

/** One line of the headline: it rises out of an invisible mask, the way film titles do. */
function RiseLine({ children, delay, reduceMotion }) {
  return (
    <span className="block overflow-hidden pb-[0.14em]">
      <motion.span
        className="block"
        initial={reduceMotion ? false : { y: '112%' }}
        animate={{ y: 0 }}
        transition={{ duration: 1.2, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  )
}

function FadeUp({ children, delay, reduceMotion, className = '' }) {
  return (
    <motion.div
      className={className}
      initial={reduceMotion ? false : { opacity: 0, y: 22 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  )
}

/**
 * Opening scene of the home page: the three finished cases fill the screen, and their lower half
 * dissolves into the page colour while the headline rises out of that darkness.
 *
 * Motion, all of it quiet:
 *  - on load the photo fades in from black while settling from a slight zoom;
 *  - on scroll the photo drifts slower than the page and darkens, so the text takes over;
 *  - film grain on top hides how soft a 1024px photo gets when stretched across a big monitor.
 * With reduced motion the scene is simply there, still.
 */
export default function HeroImmersive({ cta }) {
  const reduceMotion = useReducedMotion()
  const sectionRef = useRef(null)
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  })
  const driftY = useTransform(scrollYProgress, [0, 1], ['0%', '9%'])
  const driftScale = useTransform(scrollYProgress, [0, 1], [1, 1.08])
  const darken = useTransform(scrollYProgress, [0, 0.75], [0, 0.65])

  return (
    <section
      ref={sectionRef}
      className="relative isolate flex min-h-[100svh] flex-col justify-end overflow-hidden"
    >
      {/* Artwork: pinned to the top on phones (so the text has room), full screen on desktop. */}
      <motion.div
        className="m1nt-hero-photo absolute inset-x-0 top-0 h-[70svh] lg:-top-[12%] lg:h-[112%]"
        style={reduceMotion ? undefined : { y: driftY, scale: driftScale }}
      >
        <motion.img
          src="/home/case-collection.jpg"
          width="1024"
          height="576"
          alt="Three M1NT cases side by side, holding graded hockey, baseball and basketball cards among iridescent crystals"
          fetchPriority="high"
          decoding="async"
          draggable="false"
          className="block h-full w-full select-none object-cover object-[50%_0%]"
          initial={reduceMotion ? false : { opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 2.4, ease: EASE }}
        />
      </motion.div>

      <div className="m1nt-grain pointer-events-none absolute inset-0" aria-hidden="true" />

      {!reduceMotion && (
        <motion.div
          className="pointer-events-none absolute inset-0 bg-m1-ground"
          style={{ opacity: darken }}
          aria-hidden="true"
        />
      )}

      {/* The dissolve: the photo melts into the ground colour, and the text lives in that melt. */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[72%]"
        style={{
          background:
            'linear-gradient(to top, #0A090D 8%, rgba(10,9,13,0.9) 30%, rgba(10,9,13,0.55) 58%, rgba(10,9,13,0) 100%)',
        }}
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto w-full max-w-[1200px] px-[var(--gutter)] pb-12 pt-[calc(56px+env(safe-area-inset-top,0px)+40vh)] sm:pb-16 lg:pt-[calc(56px+env(safe-area-inset-top,0px)+30vh)]">
        <FadeUp delay={0.8} reduceMotion={reduceMotion}>
          <p className="text-[14px] text-m1-ink-soft [text-shadow:0_1px_14px_rgba(0,0,0,0.9)]">
            The future of collecting is here
          </p>
        </FadeUp>

        <h1 className="mt-4 font-m1-display text-[clamp(42px,7.4vw,108px)] font-medium leading-[1.02] tracking-[-0.015em] text-m1-ink sm:font-normal">
          <RiseLine delay={1} reduceMotion={reduceMotion}>
            M1nt Cert Platform
          </RiseLine>
        </h1>

        <div className="mt-6 grid items-end gap-8 lg:mt-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:gap-16">
          <FadeUp delay={1.4} reduceMotion={reduceMotion}>
            <p className="max-w-[30em] text-[19px] font-light leading-relaxed text-m1-ink-soft">
              Professional card grading meets blockchain technology. Authenticate, grade, and mint
              your collectible cards as NFTs.
            </p>
          </FadeUp>
          <FadeUp
            delay={1.6}
            reduceMotion={reduceMotion}
            className="flex flex-wrap items-center gap-x-6 gap-y-4"
          >
            {cta}
            <a
              href="#how-it-works"
              className="inline-flex min-h-[44px] items-center gap-2 text-[14px] tracking-[0.04em] text-m1-ink-soft transition-colors hover:text-m1-ink"
            >
              See how it works
              <i className="ri-arrow-down-line text-base" aria-hidden="true" />
            </a>
          </FadeUp>
        </div>
      </div>
    </section>
  )
}
